import thunk from 'redux-thunk';
import logger from 'redux-logger';
import { applyMiddleware, combineReducers, legacy_createStore as createStore, Store } from 'redux';
import { Provider } from 'react-redux';
import ReduxToastr, { reducer as toastrReducer } from 'react-redux-toastr';
import { IntlProvider, intlReducer } from 'react-intl-redux';
import { BrowserHistoryOptions, createBrowserHistory } from 'history';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { createReduxHistoryContext, push } from 'redux-first-history';
import App from './App';
import './styles/index.scss';
import * as serviceWorker from './serviceWorker';
import config, { isProd, isStage } from './constants/config';
import { initGoogleTagManager } from './utils/googleTagManager';
import { initUsercentrics } from './utils/usercentrics';
import { RootState } from './reducers';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import de from './constants/locales/de.json';
import it from './constants/locales/it.json';
import fr from './constants/locales/fr.json';
import en from './constants/locales/en.json';
import keycloak from './keycloak';
import { flattenMessages, removeBasketBeforeLoginIfRestored } from './utils/react';
import { sideActions } from './middlewares';
import * as actions from './actions';
import app from './reducers/app';
import localesReducer from './reducers/i18n';
import { createRoot } from 'react-dom/client';
import { ReactKeycloakProvider } from './components/KeycloakProvider/provider';

(window as any).commitHash = document.querySelector('meta[name="ui-version"]')['content'];

const LANGUAGE_STORAGE_KEY = 'apg_easy_debug_language';
const SUPPORTED_LANGUAGES = ['de', 'en', 'fr', 'it'];
const requestedLanguage = new URL(window.location.href).searchParams.get('lang');

if (requestedLanguage && SUPPORTED_LANGUAGES.includes(requestedLanguage)) {
  try {
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, requestedLanguage);

    const url = new URL(window.location.href);
    url.searchParams.delete('lang');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    console.warn('Could not apply requested language', requestedLanguage, error);
  }
}

const historyS = createBrowserHistory({
  basename: process.env.REACT_APP_PUBLIC_URL,
} as BrowserHistoryOptions);

const historySettings = {
  history: historyS,
  basename: process.env.REACT_APP_PUBLIC_URL,
};

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext(historySettings);

let middleware;

if (isProd || isStage) {
  Sentry.init({
    dsn: 'https://cbb71537e3f14a6da6191e9efb1d8073@o162224.ingest.sentry.io/5505698',
    integrations: [new Integrations.BrowserTracing()],
    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.01,
  });

  middleware = applyMiddleware(routerMiddleware, thunk, sideActions);
} else {
  // @ts-ignore
  middleware = applyMiddleware(routerMiddleware, thunk, sideActions, logger);
}

const lang = (window as any)['django']?.language || sessionStorage.getItem(LANGUAGE_STORAGE_KEY) || 'de';

// Usercentrics reads its language from <html lang>, so it must match the app locale before the CMP loads.
document.documentElement.lang = lang;

// For the standalone React app, CMP and dataLayer are set up here. For the Django-embedded version they come from Django.
if (process.env.REACT_APP_MODE !== 'django') {
  if (config.usercentricsSettingsId) {
    initUsercentrics(config.usercentricsSettingsId, lang);
  }
  initGoogleTagManager(config.gtmId);
}

const messages = {
  en: en,
  de: de,
  it: it,
  fr: fr,
};

const i18nInitialState = {
  locale: lang,
  messages: {
    ...flattenMessages(messages[lang]),
  },
};

const eventLogger = (event: unknown, error: unknown) => {
  store.dispatch(actions.keycloakMessage({ event: event }));
  if (event === 'onAuthSuccess') {
    removeBasketBeforeLoginIfRestored(store.getState().app.userDataEC.basketInfo?.basket);
  }
};

const tokenLogger = (tokens: unknown) => {
  store.dispatch(actions.keycloakMessage({ tokens: tokens }));
};

(window as any).addEventListener('keycloakReadyEvent', (evt) => {
  console.debug('keycloakReadyEvent', evt);
  store.dispatch(actions.keycloakMessage({ event: 'onReady' }));
});

(window as any).addEventListener('willLogOutDjango', (evt) => {
  store.dispatch(actions.clearData('start'));
});

(window as any).addEventListener('djangoTokensError', (evt) => {
  store.dispatch(
    push({
      search: `?error=true`,
    }),
  );
});

(window as any).addEventListener('djangoTokensRefreshed', (evt) => {
  const tokens = {
    refreshToken: (window as any).keycloak.refreshToken,
    token: (window as any).keycloak.token,
    idToken: (window as any).keycloak.idToken,
  };

  store.dispatch(actions.keycloakMessage({ tokens: tokens }));
});

export const store = createStore(
  combineReducers({
    router: routerReducer,
    app,
    intl: intlReducer,
    locales: localesReducer,
    toastr: toastrReducer,
  }),
  { intl: i18nInitialState } as any,
  middleware as any,
) as Store<RootState>;

export const history = createReduxHistory(store);

const container = document.getElementById('root');
const root = createRoot(container!);

if (process.env.REACT_APP_MODE === 'django') {
  (window as any).addEventListener('djangoScriptsReady', () => {
    setTimeout(() => {
      (window as any).keycloakReadyDjango !== true &&
        !(window as any).isKeycloakInitialising &&
        (window as any).checkKeycloakStatus();
    }, 1000);
  });

  root.render(
    <Provider store={store}>
      <IntlProvider locale='de' defaultLocale='de'>
        <Router history={history} basename={process.env.REACT_APP_PUBLIC_URL}>
          <ReduxToastr
            timeOut={4000}
            newestOnTop={false}
            preventDuplicates
            position='top-center'
            transitionIn='fadeIn'
            transitionOut='fadeOut'
            closeOnToastrClick
          />
          <App history={history} />
        </Router>
      </IntlProvider>
    </Provider>,
  );
} else {
  const keycloakProviderInitConfig = {
    onLoad: 'check-sso',
    flow: 'hybrid',
    silentCheckSsoRedirectUri: window.location.origin + process.env.REACT_APP_SILENT_URL,
    checkLoginIframe: false,
  };

  root.render(
    <Provider store={store}>
      <ReactKeycloakProvider
        authClient={keycloak}
        onEvent={eventLogger}
        onTokens={tokenLogger}
        initOptions={keycloakProviderInitConfig}
        autoRefreshToken={true}
      >
        <IntlProvider locale='de' defaultLocale='de'>
          <Router history={history} basename={process.env.REACT_APP_PUBLIC_URL}>
            <ReduxToastr
              timeOut={4000}
              newestOnTop={false}
              preventDuplicates
              position='top-center'
              transitionIn='fadeIn'
              transitionOut='fadeOut'
              closeOnToastrClick
            />
            <App history={historyS} />
          </Router>
        </IntlProvider>
      </ReactKeycloakProvider>
    </Provider>,
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
